'use client';

import { Switch } from '@headlessui/react';
import { log } from '@logtail/next';
import AnimationForm, { AnimationFormData } from 'app/animate/[[...id]]/AnimationForm';
import { AnimationCreationResult, AnimationCreationSuccess } from 'app/animate/[[...id]]/CreateAnimationTypes';
import CancelGeneration from 'app/browser/[id]/CancelGeneration';
import DeleteAnimation from 'app/browser/[id]/DeleteAnimation';
import DownloadLinks from 'app/browser/[id]/DownloadLinks';
import Frames from 'app/browser/[id]/Frames';
import ReportContent from 'app/browser/[id]/ReportContent';
import { getLatestDisplayDataForJob } from 'app/browser/[id]/ServerActions';
import AnnotatedWaveform from 'app/components/AnnotatedWaveform';

import PresetLabDynamic from 'app/components/PresetLabDynamic';
import { StyledLink } from 'app/components/StyledLink';
import { animationTypeAtom, submittingNewAnimationAtom } from 'app/components/atoms/animationFormAtoms';
import { Message } from 'app/types';
import styles from 'data/styles/styles.json';
import { Alert, Button, Label, Progress } from 'flowbite-react';
import { useAtom, useAtomValue } from 'jotai';
import { formatDate, isCompleteStatus } from 'lib/clientsafe-utils';
import { classNames } from 'lib/generic-utils';
import _, { isNumber } from 'lodash';
import { DateTime } from 'luxon';
import { useRouter } from 'next/navigation';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';
import { useEffectOnce } from 'react-use';
import useSWR from 'swr';
import PreviewPane from './PreviewPane';
import RelatedAnimations from './RecentAnimations';
import { AnimationDisplayData } from './animate-utils';
import { calculateProgress, getBestVideoUrl, getFrameUrl } from './animate-utils-client-safe';

const DATA_REFRESH_INTERVAL_MS = 10 * 1000;


type ClientComponentsProps = {
    userid?: string,
    enableAudioSync: boolean,
    isAnonymous: boolean,
    is_admin: boolean,
    initialFormData: AnimationFormData,
    initialDisplayData?: AnimationDisplayData, // undefined if there is no animation to show on inital page load (i.e. /animate)
    initialIsOwner: boolean,
    maxAudioDurationForUser: number,
}

// For when we really need a display data object but don't have one yet.
// This is for failure scenarios where we cannot do better than revert to a blank page.
const FALLBACK_DISPLAY_DATA : AnimationDisplayData = {
    id: '',
    status: '',
    isAudioReactive: false,
    width: 0,
    height: 0,
    expectedFrameCount: 0,
    displayName: '',
    artistName: '',
    createdAt: new Date(),
    basedOn: undefined,
    isConsistencyBoosted:false,
    aDiffProgress: undefined,
    artistId: '',
    updatedateAt: new Date(),
    framePattern: '',
    readyFrames: 0,
    thumbnailPattern: '',
    videoBase: '',
    videos: []
}

export default function ClientComponents({userid, enableAudioSync, isAnonymous, initialIsOwner, is_admin, initialFormData, initialDisplayData, maxAudioDurationForUser  }: ClientComponentsProps) {

    const [displayData, setDisplayData] = useState<AnimationDisplayData | undefined>(initialDisplayData);
    const animationType = useAtomValue(animationTypeAtom);
    const [errorMessage, setErrorMessage] = useState<Message | null>(null);
    
    const [showPresetLab, setShowPresetLab] = useState(false);

    const [isOwner, setIsOwner] = useState(initialIsOwner);
    const [submittingNewAnimation, setSubmittingNewAnimation] = useAtom(submittingNewAnimationAtom);
    const [progress, setProgress] = useState<number|undefined>(undefined);
    const router = useRouter();


    // States:
    // BLANK: Freshly loaded on /animate, showing placeholder
    // SUBMITTING: Just submitted creation, showing placeholder
    // PROCESSING: Showing an animation in a processing state
    // COMPLETED: Showing an animation in a completed state
    // In all states, the user may be editing form components which must not be lost as the user transitions states (unless going back to BLANK by re-accessing /animate).    
    const pageState = useMemo(() => submittingNewAnimation
        ? 'SUBMITTING'
        : !displayData?.id
        ? 'BLANK'
        : (displayData?.status && isCompleteStatus(displayData.status))
        ? 'COMPLETED'
        : 'PROCESSING', [displayData?.id, displayData?.status, submittingNewAnimation]);

    useEffectOnce(() => {
        // If hash is present, redirect to page. This should only ever happen when first loading the page,
        // never while the user is working with the page. If this isn't true, users will see a glitch,
        // and might lose form data edits, so we'd need another solution.
        const hash = window.location.hash.substring(1);
        if (hash && hash != displayData?.id) {
            router.replace(`/animate/${hash}`);
        }
    });


    // Periodically update progress bar
    useEffect(() => {
        if (!['SUBMITTING', 'PROCESSING'].includes(pageState) || !displayData) {
            return;
        }
        const intervalId = setInterval(() => { setProgress((prevProgress) => calculateProgress(displayData, prevProgress)) }, 4000);
        return () => clearInterval(intervalId);        
        
    }, [displayData, pageState]); 

    
    /////////////////////////////////////////////////////////////////////////////////////////////////////
    // Periodic display data update handling. Fetch latest display data for the current animation if necessary.

    const fetcher = useCallback(async ({ id }: { id: string }): Promise<AnimationDisplayData | Message | null> => {
        if (!id || submittingNewAnimation) {
            return null;
        }
        const refreshOutput = await getLatestDisplayDataForJob(id); // Fetch from server

        return refreshOutput;
    }, [submittingNewAnimation]);

    const shouldFetch = useCallback(() => {
        if (!displayData?.id || (displayData?.status && isCompleteStatus(displayData.status))) {
            return null; // Don't fetch if we're in a completed state or don't have an animation ID to to fetch.
        }
        return { id: displayData?.id };
    }, [displayData?.id, displayData?.status]);


    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { data, error, isLoading } = useSWR(
        shouldFetch,
        fetcher,
        {
            refreshInterval: DATA_REFRESH_INTERVAL_MS,
            revalidateOnFocus: true,
            revalidateOnReconnect: true,
            refreshWhenHidden: true,
        }
    );
    error && log.error(`Browser error: failed to fetch updated data for current animation`, {error: (error as unknown)});

    useEffect(() => {
        //@ts-expect-error - checking type
        if (data && data.id) {
            if (!_.isEqual(data, displayData)) {
                setDisplayData(data as AnimationDisplayData);
            }
        } else if (data) {
            setErrorMessage(data as Message);
        }
    }, [data, displayData]);


    /////////////////////////////////////////////////////////////////////////////////////////////////////
    // Form submission handling

    const onSubmitStarted = useCallback(() => {
        unstable_batchedUpdates(() => {
            setDisplayData(undefined);
            setSubmittingNewAnimation(true);
            setProgress(undefined);
        });
    }, [setSubmittingNewAnimation])

    const onSubmitResponded = useCallback((result: AnimationCreationResult | undefined) => {
        if (result?.message.type === 'success') {
            const startedJob = result as AnimationCreationSuccess;

            const newDisplayData : AnimationDisplayData = {
                id: startedJob.id,
                artistId: startedJob.userId,
                status: 'submitted',
                isAudioReactive: !!startedJob.parameters.metadata.audioFilename,
                width: startedJob.parameters.settings.W,
                height: startedJob.parameters.settings.H,
                expectedFrameCount: startedJob.totalFrames,
                displayName: startedJob.displayName,
                artistName: _.truncate(startedJob.owner.name || 'Unknown artist', {length:100}),
                createdAt: startedJob.createdAt,
                basedOn: startedJob.parameters.metadata.basedOn,
                isConsistencyBoosted: !!startedJob.parameters.metadata.paidOptions?.smoothing,
                aDiffProgress: undefined,
                updatedateAt: startedJob.updatedAt,
                framePattern: '',
                readyFrames: 0,
                thumbnailPattern: '',
                videoBase: '',
                videos: []                
            }

            unstable_batchedUpdates(() => {
                setDisplayData(newDisplayData);
                setIsOwner(true);
                setProgress((prevProgress) => calculateProgress(newDisplayData, prevProgress));
            });

            // this should update the URL without triggering a reload as per
            // https://nextjs.org/docs/app/building-your-application/routing/linking-and-navigating#using-the-native-history-api
            window.history.pushState({}, '', `/animate/${startedJob.id}`);
            
             // An alternative is to just update the hash fragment, but this is less clean as it requires a client-side redirect/reload
             //updateHashFragment(startedJob.id);
        } else {
            setDisplayData((value) => ({
                ...(value||FALLBACK_DISPLAY_DATA) ,
                status: 'failed',
            }));
            log.error("Job submission failure", { message: result?.message.message });
        }
        setSubmittingNewAnimation(false);
    }, [setSubmittingNewAnimation]);


    /////////////////////////////////////////////////////////////////////////////////////////////////////
    // Check for any new frames and update derived data accordingly.
    const latestFrameUrl = useMemo(() => getFrameUrl(displayData?.id, displayData?.readyFrames, displayData?.framePattern) , [displayData?.framePattern, displayData?.id, displayData?.readyFrames]);
    const bestVideoUrl = useMemo(() =>  displayData?.videos?.length ? getBestVideoUrl(displayData?.videos, displayData?.videoBase) : undefined , [displayData?.videoBase, displayData?.videos]);
    const apsectRatio = useMemo(() => (displayData?.height && displayData?.width) ? displayData.width / displayData.height : 1, [displayData?.height, displayData?.width]);


    /////////////////////////////////////////////////////////////////////////////////////////////////////
    // Memoized components

    const previewPane = useMemo(() => <PreviewPane bestVideoUrl={bestVideoUrl} latestFrameUrl={latestFrameUrl} displayData={displayData} pageState={pageState}  />, [bestVideoUrl, displayData, latestFrameUrl, pageState]); 

    const animationForm = useMemo( () => <AnimationForm
        enableAudioSync={enableAudioSync}
        is_admin={is_admin}
        isAnonymous={isAnonymous}            
        initialFormData={initialFormData}
        basedOn={displayData?.id} // Anything created with the form is based on the animation currently displayed.
        styles={styles}
        maxAudioDurationForUser={maxAudioDurationForUser}
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        onSubmitStarted={onSubmitStarted}
        onSubmitResponded={onSubmitResponded} />,
    [enableAudioSync, is_admin, isAnonymous, initialFormData, displayData?.id, onSubmitStarted, onSubmitResponded, maxAudioDurationForUser]);

    const progressBar = useMemo(() => {
        if (pageState !== 'PROCESSING' || !displayData || !isNumber) {
            return <></>;
        }

        return <div className='grow mr-4'>
            {isNumber(progress) && <Progress className='w-full' size="xl" color="dark" labelProgress textLabelPosition="outside" progress={progress} /> }
        </div>

    }, [displayData, pageState, progress])

    const downloadLinks = useMemo(() => {
        if (pageState !== 'COMPLETED' || !isOwner || !displayData) {
            return <></>
        }
        return <DownloadLinks displayData={displayData} />
    }, [displayData, isOwner, pageState])

    const cancelButton = useMemo(() => {
        if (!displayData?.id || isCompleteStatus(displayData?.status) || !(isOwner || is_admin)) {
            return <></>
        }
        return <CancelGeneration isOwner={isOwner} jobId={displayData.id} />
    }, [displayData?.id, displayData?.status, isOwner, is_admin])

    const reportButton = useMemo(() => {
        if (!displayData?.id || displayData.readyFrames < 1) {
            return <></>
        }
        return <ReportContent alreadyReported={false} jobId={displayData.id} />
    }, [displayData?.id, displayData?.readyFrames])    


    const deleteButton = useMemo(() => {
        if (!displayData?.id  || !(isOwner || is_admin)) {
            return <></>
        }        
        return <DeleteAnimation displayName={displayData.displayName} isOwner={isOwner} jobId={displayData.id} />
    }, [displayData?.displayName, displayData?.id, isOwner, is_admin])


    const animationInfoText = useMemo(() => {
        if (pageState === 'SUBMITTING' || pageState === 'BLANK' || !displayData) {
            return <></>
        }
        return <div className='flex flex-col gap-y-1 justify-self-end self-end md:justify-end text-center md:items-end md:text-right'>
            <div className='text-xs'>by {displayData?.artistName}</div>
            <div className='text-xs' suppressHydrationWarning>{formatDate(displayData.createdAt)} ({DateTime.fromJSDate(displayData.createdAt).toRelative()})</div>
            <div className='text-xs'>
                <span className='font-bold'>ID: </span>{displayData.displayName}
                {displayData.basedOn ? <>,&nbsp;<StyledLink discrete href={`/animate/${displayData.basedOn}`}>forked from this</StyledLink>.</> : <></>}
            </div>
        </div>
    }, [displayData, pageState]);
                

    /////////////////////////////////////////////////////////////////////////////////////////////////////
    // Page rendering

    return (
        <>
            {/* Create & preview */}
            <div className={classNames('flex flex-col-reverse lg:flex-row flex-wrap-reverse justify-center gap-x-4 gap-y-4 items-center lg:items-end')}>
                <div className={classNames('w-full h-full transition-all delay-50 duration-300 overflow-hidden', 'max-h-[2000px] max-w-2xl')}>
                    <div className={classNames('h-full grow px-2 py-2 max-w-[42rem] lg:min-w-[40rem] flex flex-col justify-start items-start')}>
                        {animationForm}
                        {isAnonymous && <Button href='/begin' className='mt-4 rounded-none bg-black text-white dark:bg-white dark:text-black border-black border-solid dark:border-white px-3 py-1 hover:scale-x-110 hover:ease-linear hover:duration-300 w-full'>
                            Sign in to create your own animations.
                        </Button>}
                    </div>
                </div>

                
                <div className={classNames("w-full lg:w-1/2", apsectRatio === 1 ? 'max-w-[540px] lg:min-w-[512px]' : apsectRatio > 1 ? 'max-w-[1024px] lg:min-w-[768px]' : 'max-w-[540px] lg:min-w-[440px]'  )}> 
                    <div
                        className='flex flex-col text-center items-center justify-center flex-shrink mx-auto'
                        >
                        {previewPane}
                    </div>
                    <div className='mt-2 flex flex-row justify-between w-full'>
                        <div className='flex flex-row gap-x-2 w-full'>
                            {progressBar}
                            {downloadLinks}
                        </div>
                        <div className='flex flex-row gap-x-2 ml-4'>
                            {cancelButton}
                            {reportButton}
                            {deleteButton}
                        </div>
                        <div>
                            {isLoading? '...' : ''}
                        </div>
                    </div>
                    
                    {animationInfoText}
                </div>
            </div>
            <div>
                {errorMessage && <Alert key={errorMessage.timestamp} color={errorMessage.type}>{errorMessage.message}</Alert>}
            </div>
            
            {(animationType == 'audio-reactive') &&
                <div className="max-w-7xl mx-auto mt-4">
                    <AnnotatedWaveform maxAudioDurationForUser={maxAudioDurationForUser}/>
                    {is_admin && <>
                        <div className='flex flex-row items-center gap-2 mt-4 mb-4 justify-left '>
                                <Label htmlFor={"showPresetLab"} className="block text-neutral-700 dark:text-neutral-400">
                                    Preset Lab (admin only & WIP):
                                </Label>                        
                                <Switch
                                    name="showPresetLab"
                                    checked={showPresetLab}
                                    onClick={() =>setShowPresetLab(val => !val)}
                                    className="mx-2 relative inline-flex h-2 w-8 items-center rounded-none ui-checked:bg-blue-400 ui-not-checked:bg-neutral-400 ui-not-checked:dark:bg-neutral-600">
                                    <span className="inline-block h-5 w-5 transform rounded-sm bg-neutral-500 dark:bg-neutral-400 transition ui-checked:translate-x-6 ui-not-checked:-translate-x-1" />
                                </Switch>
                        </div>
                        {showPresetLab && <PresetLabDynamic />}
                    
                    </>
                    }
                </div>
            }

            <div>
                {(isOwner && displayData && displayData.readyFrames > 0) ? <Frames jobId={displayData.id} frameCount={displayData.readyFrames} framePattern={displayData.framePattern} thumbnailPattern={displayData.thumbnailPattern} /> : <></>}
            </div>

            {(displayData?.artistId || userid) && <div className="mx-auto">
                <h2 className="mt-3 text-xl py-6">
                    {(displayData?.artistId && (displayData.artistId !== userid)) ? <>Related Animations</> : <>Your Recent Animations</>}
                </h2>
                <RelatedAnimations userId={(displayData?.artistId || userid)!} excludeJobId={displayData?.id} />
            </div>}
        </>)


}
